import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, Globe2, Store, MoonStar, Bus,
  Stethoscope, Target, Users, HeartHandshake, FileCheck, Lock,
  Eye, RefreshCw, ClipboardCheck, UserCheck, Building2, Landmark,
  GraduationCap, Check, Scale, Handshake, Newspaper,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import Marquee from "../components/Marquee";
import EnquiryForm from "../components/EnquiryForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const GOLD = "#C5A059";
const GREEN = "#0F382C";
const TEAL = "#0D9488";

const TRUST = [
  "Named clinical oversight",
  "GPhC-registered pharmacy professionals",
  "Reporting on every engagement",
  "Safeguarding-aware delivery",
  "UK & Gulf partnerships",
];

const DIVISIONS = [
  { icon: Stethoscope, accent: GREEN, to: "/commission-clinical-services", tag: "DIV 01",
    image: "/assets/about-pharmacist-practice-team.png",
    name: "Primary Care & Clinical Services",
    desc: "Clinical pharmacist and pharmacy technician services for GP practices, PCNs and federations — medicines optimisation, SMRs and prescribing safety.",
    testid: "division-card-primary-care" },
  { icon: Store, accent: TEAL, to: "/community-pharmacy", tag: "DIV 02",
    image: "/assets/cp-hero.png",
    name: "Community Pharmacy",
    desc: "Flexible pharmacist workforce solutions — urgent locum cover, short-term and long-term placements for community pharmacies across the UK.",
    testid: "division-card-community" },
  { icon: MoonStar, accent: GOLD, to: "/out-of-hours", tag: "DIV 03",
    image: "/assets/ooh-hero.png",
    name: "Out of Hours",
    desc: "A dedicated 9 pm – 8 am medication supply and delivery pathway supporting palliative care patients across the Wigan area.",
    testid: "division-card-ooh" },
  { icon: Bus, accent: GREEN, to: "/transport", tag: "DIV 04",
    image: "/assets/transport-division.png",
    name: "Transport",
    desc: "Safe, consistent passenger transport — home-to-school, SEND and specialist transport for local authorities, schools and families.",
    testid: "division-card-transport" },
  { icon: Globe2, accent: TEAL, to: "/international", tag: "INTL",
    image: "/assets/skyline-dubai.png",
    name: "International Development",
    desc: "Exploring clinical pharmacy consultancy, medicines optimisation and workforce partnerships across the UAE, Qatar and Oman.",
    testid: "division-card-international" },
  { icon: Building2, accent: GOLD, to: "/ncs-clinic", tag: "DEV",
    image: "/assets/clinic-hero.png",
    name: "NCS Clinic",
    badge: "In development",
    desc: "A developing national network of private, pharmacist-led clinics — register your interest as a founding partner.",
    testid: "division-card-clinic" },
];

const ETHOS = [
  { icon: Scale, title: "Clinical Excellence & Governance",
    text: "Every service is designed around evidence-based practice, named clinical oversight and clearly defined professional standards — governance first, always." },
  { icon: ShieldCheck, title: "Operational Reliability",
    text: "Dependable delivery through qualified, verified professionals and disciplined processes — capacity where system demand is highest." },
  { icon: HeartHandshake, title: "Patient-Centred Ethics",
    text: "Accessible, unhurried and compassionate care across every division — the patient's experience and safety come before anything else." },
  { icon: Handshake, title: "System Integration",
    text: "We work within the teams we support — GP practices, PCNs, NHS organisations and commissioners — as partners, not suppliers." },
];

const VISION = [
  "Expand clinical pharmacy services across UK primary care",
  "Develop long-term NHS and ICB partnerships",
  "Strengthen community pharmacy workforce solutions",
  "Grow contracted school and specialist transport services",
  "Explore healthcare partnerships in Dubai, Qatar and Oman",
  "Invest in service quality, workforce development and operational capability",
];

const GOVERNANCE = [
  { icon: UserCheck, title: "Professional registration & competency checks",
    text: "Registration status and role-relevant competence verified before any placement." },
  { icon: Stethoscope, title: "Clinical governance",
    text: "Named clinical oversight, defined scope of practice and clear escalation routes." },
  { icon: ShieldCheck, title: "Safeguarding",
    text: "Safeguarding-aware staff and appropriate checks for patient- and passenger-facing roles." },
  { icon: Lock, title: "Information governance",
    text: "Confidential handling of personal and clinical information across every service." },
  { icon: Eye, title: "Service oversight",
    text: "Active monitoring of delivery, communication and service quality on every engagement." },
  { icon: RefreshCw, title: "Quality improvement",
    text: "Structured review of feedback, incidents and outcomes to keep services improving." },
  { icon: ClipboardCheck, title: "Operational & transport compliance",
    text: "Appropriate licensing, insurance, vehicle and driver compliance for transport operations." },
];

const PARTNERS_TODAY = [
  { icon: Stethoscope, name: "GP Practices" },
  { icon: Users, name: "Primary Care Networks" },
  { icon: Landmark, name: "NHS Organisations" },
];

const PARTNERS_BUILDING = [
  { icon: Landmark, name: "Integrated Care Boards" },
  { icon: Store, name: "Community Pharmacy Groups" },
  { icon: Building2, name: "Local Authorities" },
  { icon: GraduationCap, name: "Educational Establishments" },
  { icon: Globe2, name: "International Healthcare Providers" },
];

const Overline = ({ children, light = false }) => (
  <p className={`font-mono-brand text-[11px] uppercase tracking-[0.22em] ${light ? "text-[#C5A059]" : "text-[#0F382C]"}`}>
    {children}
  </p>
);

const Home = () => {
  const [insights, setInsights] = useState([]);
  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setInsights((r.data || []).slice(0, 3))).catch(() => {});
  }, []);

  return (
  <main>
    <Seo
      title="NCS — National Clinical Services | Clinical, Pharmacy, Transport & International Services"
      description="National Clinical Services is a UK-founded, multi-service organisation: clinical pharmacy expertise, healthcare workforce solutions, community pharmacy support, out-of-hours medication services and specialist passenger transport."
    />

    {/* 1 — EDITORIAL HERO */}
    <section className="relative overflow-hidden border-b border-[#0B192C]/10" data-testid="corporate-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-mono-brand text-[11px] uppercase tracking-[0.24em] text-[#0F382C]">
              National Clinical Services — United Kingdom
            </motion.p>
            <h1 data-testid="hero-heading"
              className="mt-5 font-display font-medium tracking-tight leading-[1.08] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              <MaskedLine delay={0.15}>Clinical excellence, delivered</MaskedLine>
              <MaskedLine delay={0.3}>with <span className="font-serif-accent">institutional discipline.</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-xl">
              From primary care and clinical pharmacy to specialist transport and international
              healthcare development, NCS brings every service under one accountable organisation —
              governed by clinical standards, run with commercial discipline.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65 }}
              className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => scrollToId("divisions")} data-testid="hero-divisions-btn"
                className="group inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-medium px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C]">
                Explore Our Divisions
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link to="/about" data-testid="hero-discover-btn"
                className="inline-flex items-center gap-2.5 text-[#0B192C] font-medium px-7 py-4 rounded-full text-sm border border-[#0B192C]/20 transition-colors duration-300 hover:border-[#0F382C] hover:text-[#0F382C]">
                The NCS Ethos
              </Link>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35 }}
            className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-3 -left-3 right-3 bottom-3 border border-[#C5A059]/50 rounded-2xl pointer-events-none" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden border border-[#0B192C]/10 shadow-[0_24px_60px_-24px_rgba(11,25,44,0.25)]">
                <img src="/assets/corp-hero.png" alt="National Clinical Services corporate headquarters visual"
                  className="w-full h-[300px] sm:h-[380px] object-cover" />
              </div>
              <div className="absolute -bottom-5 left-6 bg-white border border-[#0B192C]/10 rounded-xl px-5 py-3.5 shadow-[0_12px_30px_-12px_rgba(11,25,44,0.2)]">
                <p className="font-mono-brand text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">One organisation</p>
                <p className="mt-0.5 text-sm font-semibold text-[#0B192C]">Six specialist divisions</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* TRUST STRIP */}
    <section className="border-b border-[#0B192C]/10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <Reveal>
          <div data-testid="trust-strip" className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2.5">
            {TRUST.map((t, i) => (
              <span key={t} className="inline-flex items-center gap-3">
                {i > 0 && <span className="hidden sm:inline-block w-px h-3.5 bg-[#0B192C]/15" aria-hidden="true" />}
                <span className="w-1 h-1 rounded-full bg-[#C5A059]" aria-hidden="true" />
                <span className="text-xs sm:text-[13px] font-medium text-[#0B192C] whitespace-nowrap">{t}</span>
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <Marquee />

    {/* 2 — THE PRINCIPLES WE OPERATE BY (replaces founder/leadership) */}
    <section id="ethos" className="py-20 sm:py-28 lg:py-32" data-testid="home-ethos-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>The principles we operate by</Overline>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl">
              An organisation defined by its standards, not its personalities
            </h2>
            <p className="text-sm leading-relaxed text-[#475569] max-w-md">
              NCS is built to outlast any individual. What you experience is a framework — clinical
              governance, disciplined operations and a clear sense of duty — applied consistently
              across every division.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {ETHOS.map((e, i) => (
            <Reveal key={e.title} delay={(i % 4) * 0.07}>
              <div data-testid={`pillar-card-${i + 1}`}
                className={`group h-full bg-white border border-[#0B192C]/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_44px_-18px_rgba(11,25,44,0.18)] hover:border-[#0B192C]/20 ${i % 2 === 0 ? "hairline-gold" : "hairline-green"}`}>
                <span className="flex items-center justify-between">
                  <e.icon size={22} className="text-[#0F382C]" />
                  <span className="font-mono-brand text-[10px] tracking-[0.18em] text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
                </span>
                <h3 className="mt-6 font-display text-xl font-semibold text-[#0B192C] leading-snug">{e.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#475569]">{e.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 3 — OUR DIVISIONS */}
    <section id="divisions" className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Our group at a glance</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl">
              Expertise across services. One commitment to excellence.
            </h2>
          </div>
          <p className="text-sm leading-relaxed text-[#475569] max-w-md">
            National Clinical Services is a UK-founded, multi-service organisation bringing together
            clinical pharmacy expertise, healthcare workforce solutions, community pharmacy support,
            out-of-hours medication services and specialist passenger transport.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {DIVISIONS.map((d, i) => (
            <Reveal key={d.name} delay={(i % 3) * 0.08}>
              <Link to={d.to} data-testid={d.testid}
                className="group flex flex-col h-full bg-[#FBFBFA] border border-[#0B192C]/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_48px_-16px_rgba(11,25,44,0.18)] hover:border-[#0B192C]/25">
                <div className="relative h-48 overflow-hidden">
                  <img src={d.image} alt={d.name} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#0B192C]/25" />
                  <span className="absolute top-4 left-4 font-mono-brand text-[10px] tracking-[0.18em] text-white bg-[#0B192C]/70 backdrop-blur px-2.5 py-1.5">
                    {d.tag}
                  </span>
                  {d.badge && (
                    <span className="absolute top-4 right-4 bg-[#C5A059] text-[#0B192C] font-mono-brand text-[9px] uppercase tracking-[0.14em] px-2.5 py-1.5">
                      {d.badge}
                    </span>
                  )}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="w-8 h-px mb-5" style={{ backgroundColor: d.accent }} />
                  <h3 className="font-display text-xl font-semibold text-[#0B192C] leading-snug">{d.name}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569] flex-1">{d.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-[#0B192C] transition-colors duration-300 group-hover:text-[#0F382C]">
                    Explore this division
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 4 — OUR EXPERIENCE */}
    <section id="experience" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <Overline>Our experience</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C]">
            Built on experience. Focused on the future.
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569] max-w-2xl">
            <p>
              NCS grew from frontline clinical work — supporting GP practices, primary care networks
              and community healthcare with medicines optimisation, structured medication reviews and
              prescribing safety programmes.
            </p>
            <p>
              That clinical foundation now extends into operational delivery: for over five years we
              have supported the out-of-hours palliative care medication pathway in the Wigan area,
              coordinating prescription collection, authorised pharmacy dispensing and overnight
              delivery to patients' homes.
            </p>
            <p>
              Every service we build starts the same way — with professional knowledge, dependable
              processes and clear accountability.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-5">
          <div className="border-l-2 border-[#C5A059] pl-8 space-y-8" data-testid="experience-stats">
            <div>
              <p className="font-display text-4xl sm:text-5xl font-semibold text-[#0B192C]">5+ years</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">supporting out-of-hours palliative care medication services in the Wigan area</p>
            </div>
            <div>
              <p className="font-display text-4xl sm:text-5xl font-semibold text-[#0B192C]">9 pm – 8 am</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">overnight on-call cover, every night of the year</p>
            </div>
            <div>
              <p className="font-display text-4xl sm:text-5xl font-semibold text-[#0B192C]">6 divisions</p>
              <p className="mt-1.5 text-sm leading-relaxed text-[#475569]">specialist service divisions under one accountable organisation</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* 5 — OUR VISION 2030 */}
    <section className="py-20 sm:py-28 lg:py-32 bg-[#0B192C] relative overflow-hidden">
      <div className="absolute inset-0 dots-bg opacity-[0.15] pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline light><span className="inline-flex items-center gap-2"><Target size={13} /> Our Vision 2030</span></Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white max-w-2xl">
              Building the next generation of specialist services
            </h2>
          </div>
          <p className="text-sm text-white/50 max-w-md">
            Our strategic ambitions for the years ahead — where we are heading, not statements of
            where we already are.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {VISION.map((v, i) => (
            <Reveal key={v} delay={(i % 3) * 0.08}>
              <div data-testid={`vision-card-${i + 1}`}
                className="h-full bg-[#0B192C] p-7 transition-colors duration-300 hover:bg-[#0D1E36]">
                <span className="flex items-center justify-between">
                  <Check size={16} className="text-[#C5A059]" />
                  <span className="font-mono-brand text-[10px] tracking-[0.18em] text-white/25">{String(i + 1).padStart(2, "0")}</span>
                </span>
                <p className="mt-4 text-sm sm:text-base font-medium leading-relaxed text-white/85">{v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 6 — CORPORATE GOVERNANCE & QUALITY */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-b border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <Overline>Corporate governance & quality</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl mx-auto">
            Governed like a healthcare organisation. Run like a business.
          </h2>
          <p className="mt-4 text-base text-[#475569] max-w-2xl mx-auto">
            Every NCS service operates within a defined governance framework, so commissioners and
            partners always know who is accountable and how quality is maintained.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0B192C]/10 border border-[#0B192C]/10">
          {GOVERNANCE.map((g, i) => (
            <Reveal key={g.title} delay={(i % 3) * 0.08}>
              <div data-testid={`governance-card-${i + 1}`}
                className="h-full flex items-start gap-4 bg-white p-6 sm:p-7 transition-colors duration-300 hover:bg-[#F4F4F0]">
                <span className="w-10 h-10 bg-[#0B192C] text-[#C5A059] flex items-center justify-center shrink-0">
                  <g.icon size={18} />
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-[#0B192C] leading-snug">{g.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-[#475569]">{g.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* 7 — CASE STUDY + INSIGHTS PREVIEW */}
    <section className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-5">
            <Link to="/case-study" data-testid="home-case-study-preview"
              className="group relative block h-full min-h-[340px] overflow-hidden border border-[#0B192C]/10">
              <img src="/assets/cs-hero.png" alt="Medicines optimisation case study" loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-[#0B192C]/70" />
              <div className="relative h-full flex flex-col justify-end p-8 sm:p-10">
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Case study</p>
                <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-white leading-snug">
                  Medicines optimisation in practice
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70 max-w-sm">
                  How structured medication reviews and prescribing safety work translate into
                  measurable value for primary care.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-white group-hover:text-[#C5A059] transition-colors">
                  Read the case study <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="h-full bg-white border border-[#0B192C]/10 p-8 sm:p-10" data-testid="home-insights-preview">
              <div className="flex items-center justify-between gap-4">
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#0F382C] inline-flex items-center gap-2">
                  <Newspaper size={13} /> Latest insights
                </p>
                <Link to="/insights" data-testid="home-insights-all"
                  className="text-[13px] font-semibold text-[#0B192C] hover:text-[#0F382C] transition-colors inline-flex items-center gap-1.5">
                  All articles <ArrowUpRight size={13} />
                </Link>
              </div>
              <div className="mt-6 divide-y divide-[#0B192C]/10">
                {insights.map((a) => (
                  <Link key={a.slug || a.id} to={`/insights/${a.slug}`} data-testid={`home-insight-${a.slug}`}
                    className="group flex items-start justify-between gap-6 py-5 first:pt-0 last:pb-0">
                    <div>
                      <p className="font-mono-brand text-[10px] uppercase tracking-[0.16em] text-[#94A3B8]">{a.category}</p>
                      <h4 className="mt-1.5 font-display text-lg sm:text-xl font-semibold text-[#0B192C] leading-snug group-hover:text-[#0F382C] transition-colors">
                        {a.title}
                      </h4>
                    </div>
                    <ArrowUpRight size={18} className="mt-1 shrink-0 text-[#94A3B8] transition-all duration-300 group-hover:text-[#C5A059] group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
                {insights.length === 0 && (
                  <p className="py-5 text-sm text-[#64748B]">Clinical and operational perspectives from across the NCS group.</p>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* 8 — OUR PARTNERS & COMMISSIONERS */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-y border-[#0B192C]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>Our partners & commissioners</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C] max-w-2xl">
            Trusted where it matters. Building where we're needed next.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full border border-[#0B192C]/10 p-8 hairline-green" data-testid="partners-today">
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#0F382C]">
                Organisations we support today
              </p>
              <div className="mt-6 divide-y divide-[#0B192C]/10">
                {PARTNERS_TODAY.map((p) => (
                  <div key={p.name} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                    <span className="w-10 h-10 bg-[#0F382C]/10 text-[#0F382C] flex items-center justify-center shrink-0">
                      <p.icon size={18} />
                    </span>
                    <p className="font-display text-base sm:text-lg font-semibold text-[#0B192C]">{p.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full border border-[#0B192C]/10 p-8 hairline-gold" data-testid="partners-building">
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">
                Organisations we're building partnerships with
              </p>
              <div className="mt-6 divide-y divide-[#0B192C]/10">
                {PARTNERS_BUILDING.map((p) => (
                  <div key={p.name} className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                    <span className="w-10 h-10 bg-[#C5A059]/10 text-[#A98342] flex items-center justify-center shrink-0">
                      <p.icon size={18} />
                    </span>
                    <p className="font-display text-base sm:text-lg font-semibold text-[#0B192C]">{p.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-6">
          <p className="text-xs leading-relaxed text-[#94A3B8] max-w-2xl">
            Existing relationships reflect services NCS currently delivers; developing partnerships
            are active conversations and ambitions. Organisation names and logos are used only with
            permission.
          </p>
        </Reveal>
      </div>
    </section>

    {/* 9 — WORK WITH NCS */}
    <section className="py-20 sm:py-28 lg:py-32 bg-[#0F382C] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <Reveal>
            <Overline light>Work with NCS</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Partner with an organisation built for long-term service delivery
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/65 max-w-2xl">
              Whether you are commissioning clinical services, seeking specialist workforce support or
              exploring a transport partnership, NCS welcomes the opportunity to discuss your requirements.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="cta-discuss-partnership"
                className="group inline-flex items-center gap-2.5 bg-[#C5A059] text-[#0B192C] font-semibold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-white">
                Discuss a Partnership
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/commission-clinical-services" data-testid="cta-request-proposal"
                className="inline-flex items-center gap-2.5 text-white font-medium px-7 py-4 rounded-full text-sm border border-white/30 transition-colors duration-300 hover:bg-white hover:text-[#0B192C]">
                Request a Proposal
              </Link>
              <button onClick={() => scrollToId("contact")} data-testid="cta-contact-team"
                className="inline-flex items-center gap-2.5 text-white font-medium px-7 py-4 rounded-full text-sm border border-white/30 transition-colors duration-300 hover:bg-white hover:text-[#0B192C]">
                Contact Our Team
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* CONTACT FORM (existing functionality, preserved) */}
    <section className="py-20 sm:py-28 lg:py-32 bg-white border-t border-[#0B192C]/10" id="contact">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-10">
          <Overline>Get in touch</Overline>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0B192C]">
            Speak to our team
          </h2>
          <p className="mt-4 text-base text-[#475569] max-w-xl">
            Tell us what you're working towards — a real person from our team will respond,
            usually within one working day.
          </p>
        </Reveal>
        <Reveal>
          <EnquiryForm journey="general" heading="Speak to Our Clinical Services Team" />
        </Reveal>
      </div>
    </section>
  </main>
  );
};

export default Home;
